import { Component } from '@theme/component';
import { QuickAddComponent } from '@theme/quick-add';
import { ThemeEvents, VariantUpdateEvent, CartAddEvent } from '@theme/events';
import { formatMoneyFromCents } from '@theme/paint-calculator-core';
import { getBundledCartSectionIds } from './cart-section-ids.js';

/**
 * Aanvullende artikelen / toebehoren per variant (SHO-8348).
 *
 * Toont ÉÉN gecombineerde lijst toebehoren van de geselecteerde variant (alle gekoppelde
 * groepen samengevoegd, gededupliceerd per product). Elk uniek product staat als <template>
 * in de DOM + een variant→{title,items}-map; bij `variant:update` clonen we de juiste product-
 * templates in de live-container.
 *
 * Selectiemodel via vinkje + aantal: aantal 0 / uitgevinkt = niet geselecteerd.
 * Vinkje aan zet aantal op minimaal 1; stepper past aantal aan (0 = uitvinken).
 * Klik op de kaart (behalve stepper / variant-picker) toggelt selectie. Klik op afbeelding/titel
 * vinkt aan én opent de quick-add modal. Per item: inline variantkeuze (pills of dropdowns).
 *
 * De geselecteerde toebehoren worden NIET los toegevoegd: ze liften mee op de hoofd-
 * "In winkelwagen" — wanneer het hoofd-product-form zijn succes-`CartAddEvent` (source
 * `product-form-component`) vuurt, of bij configurator-PDP's op `addToCart:success`
 * (ConfiguratorNestedCart omzeilt het theme-form), voegen we alle geselecteerde
 * toebehoren in één batched /cart/add toe.
 *
 * @extends {Component}
 */
/**
 * @typedef {{ id: string, price: number, available: boolean, options: string[], image?: string, productUrl?: string }} VapVariant
 * @typedef {{ options: string[], variants: VapVariant[], fallbackImage?: string }} VapVariantData
 */

/** Aantal thumbnails in de ingeklapte kop; meer past niet naast de tekst. */
const PREVIEW_THUMB_COUNT = 3;

/**
 * Producttitel inkorten voor de namen-preview in de kop: alles vóór de eerste
 * " - " is de kern ("Stevig klusmes softgrip - 3 sets afbreekmessen 18 mm").
 * @param {string} raw
 * @returns {string}
 */
function shortenTitle(raw) {
  const [head] = raw.trim().split(' - ');
  return (head ?? '').trim();
}

export class VariantAdditionalProducts extends Component {
  /** @type {Record<string, { title?: string, items: string[], defaultOpen?: boolean }>} */
  #dataByVariantId = {};

  /** @type {Map<string, HTMLTemplateElement>} */
  #templatesByProductId = new Map();

  /** @type {Map<string, VapVariantData>} */
  #variantDataByProductId = new Map();

  /** @type {string | undefined} */
  #currentVariantId = undefined;

  /** @type {(() => void) | undefined} */
  #teardownVariantInput = undefined;

  /** @type {QuickAddComponent | null} */
  #quickAdd = null;

  /** @type {Map<string, string>} */
  #variantChoiceByProductId = new Map();

  /** @type {WeakMap<HTMLElement, VapVariantData>} */
  #variantDataByItem = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.#reloadFromDom();
    this.#quickAdd = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.addEventListener('addToCart:success', this.#onConfiguratorCartSuccess);
    this.#bindVariantInputFallback();

    this.#applyVariantId(this.#resolveInitialVariantId());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    document.removeEventListener('addToCart:success', this.#onConfiguratorCartSuccess);
    this.#teardownVariantInput?.();
    this.#teardownVariantInput = undefined;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reloadFromDom();
    this.#quickAdd = /** @type {QuickAddComponent | null} */ (this.querySelector('quick-add-component'));
    this.#applyVariantId(this.#resolveInitialVariantId());
  }

  /** Herbouw map + product-templates uit de DOM. */
  #reloadFromDom() {
    const payload = this.querySelector('script[type="application/json"].vap__map');
    try {
      const parsed = payload?.textContent ? JSON.parse(payload.textContent) : {};
      this.#dataByVariantId = parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      this.#dataByVariantId = {};
    }

    this.#templatesByProductId = new Map();
    for (const tpl of this.querySelectorAll('template.vap__prod')) {
      const pid = tpl.getAttribute('data-product-id');
      if (pid && tpl instanceof HTMLTemplateElement) this.#templatesByProductId.set(pid, tpl);
    }

    this.#variantDataByProductId = new Map();
    for (const script of this.querySelectorAll('script.vap__variant-data')) {
      const pid = script.getAttribute('data-product-id');
      if (!pid) continue;
      try {
        const parsed = script.textContent ? JSON.parse(script.textContent) : null;
        if (parsed && Array.isArray(parsed.variants)) {
          this.#variantDataByProductId.set(pid, parsed);
        }
      } catch {
        /* ignore invalid variant JSON */
      }
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (!(event instanceof VariantUpdateEvent)) return;

    if (event.target instanceof Element && event.target.closest('#quick-add-dialog, .quick-add-modal')) {
      return;
    }

    const id = event.detail.resource?.id;
    if (id == null || id === '') return;

    const key = String(id);
    if (!(key in this.#dataByVariantId)) return;

    this.#applyVariantId(key);
  };

  #productFormScope() {
    return (
      this.closest('product-form-component') ??
      this.closest('.product-information')?.querySelector('product-form-component') ??
      this.closest('.product-details')?.querySelector('product-form-component')
    );
  }

  #resolveMainVariantId() {
    const productForm = this.#productFormScope();
    if (productForm instanceof Element) {
      const form = productForm.querySelector('form[action*="/cart/add"]');
      if (form instanceof HTMLFormElement) return this.#resolveMainVariantIdFromForm(form);
    }
    return null;
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {string | null}
   */
  #resolveMainVariantIdFromForm(form) {
    const variantIdInput =
      form.querySelector('[ref="variantId"]') ?? form.querySelector('input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement && variantIdInput.value) return variantIdInput.value;
    return null;
  }

  /**
   * @param {HTMLFormElement} form
   */
  #isConfiguratorForm(form) {
    if (form.querySelector('input[name="properties[_configurator_config]"]')) return true;
    return Boolean(form.closest('.product-configurator-section'));
  }

  /**
   * @param {HTMLFormElement} form
   */
  #isFormInProductScope(form) {
    const scope = this.#productFormScope();
    return scope instanceof Element && scope.contains(form);
  }

  #resolveInitialVariantId() {
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    if (fromUrl) return fromUrl;

    const fromDataset = this.dataset.initialVariantId;
    if (fromDataset) return fromDataset;

    return this.#resolveMainVariantId();
  }

  #bindVariantInputFallback() {
    const productForm = this.#productFormScope();
    const variantIdInput =
      productForm?.querySelector('[ref="variantId"]') ?? productForm?.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement)) return;

    const onChange = () => {
      if (variantIdInput.value) this.#applyVariantId(variantIdInput.value);
    };
    variantIdInput.addEventListener('change', onChange);

    const observer = new MutationObserver(() => {
      if (variantIdInput.value) onChange();
    });
    observer.observe(variantIdInput, { attributes: true, attributeFilter: ['value'] });

    this.#teardownVariantInput = () => {
      variantIdInput.removeEventListener('change', onChange);
      observer.disconnect();
    };
  }

  /**
   * Render de gecombineerde toebehoren-lijst van de actieve variant in de live-container.
   * @param {string | null} variantId
   */
  #applyVariantId(variantId) {
    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    const key = variantId ? String(variantId) : '';
    const entry = (key && this.#dataByVariantId[key]) || null;
    const items = entry && Array.isArray(entry.items) ? entry.items : [];

    if (!items.length) {
      this.#currentVariantId = key;
      live.replaceChildren();
      this.hidden = true;
      return;
    }

    // Zelfde variant + al gerenderd → niet herbouwen, zodat de selectie behouden
    // blijft bij spurieuze variant:update's (anders wist replaceChildren selectie/aantallen).
    if (key === this.#currentVariantId && live.childElementCount > 0) {
      this.hidden = false;
      this.#syncPanelOpen(entry);
      const hasVisiblePicker = live.querySelector('[data-vap-variants]:not([hidden])');
      if (!hasVisiblePicker) {
        this.#initVariantPickers(live);
      }
      return;
    }
    this.#currentVariantId = key;

    this.#snapshotVariantChoices(live);
    live.replaceChildren();
    for (const pid of items) {
      const tpl = this.#templatesByProductId.get(String(pid));
      if (tpl) live.appendChild(tpl.content.cloneNode(true));
    }

    this.hidden = live.childElementCount === 0;
    this.#syncPanelOpen(entry);
    this.#initVariantPickers(live);
    this.#updateSummaryPreview();
    this.#recompute();
  }

  /**
   * Open/dicht van het details-paneel: metaobject `standaard_open` of theme-blokinstelling.
   * @param {{ defaultOpen?: boolean } | null} entry
   */
  #syncPanelOpen(entry) {
    const panel = this.querySelector('[data-vap-panel]');
    if (!(panel instanceof HTMLDetailsElement)) return;

    const blockDefault = this.dataset.expandedDefault === 'true';
    const metaOpen = entry?.defaultOpen === true;
    panel.open = metaOpen || blockDefault;
  }

  /**
   * Onthoud inline variantkeuze vóór rebuild van de live-lijst.
   * @param {HTMLElement} live
   */
  #snapshotVariantChoices(live) {
    for (const item of live.querySelectorAll('[data-vap-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const pid = item.dataset.productId;
      const vid = item.dataset.variantId;
      if (pid && vid) this.#variantChoiceByProductId.set(pid, vid);
    }
  }

  /**
   * Initialiseer inline variant-pickers op alle items in scope.
   * @param {ParentNode} scope
   */
  #initVariantPickers(scope) {
    const items = scope.querySelectorAll('[data-vap-item]');

    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      const data = this.#parseVariantData(item);

      if (!data) {
        const wrap = item.querySelector('[data-vap-variants]');
        if (wrap instanceof HTMLElement) wrap.hidden = true;
        continue;
      }

      if (data.variants.length <= 1) {
        const wrap = item.querySelector('[data-vap-variants]');
        if (wrap instanceof HTMLElement) wrap.hidden = true;
        continue;
      }

      this.#variantDataByItem.set(item, data);
      this.#buildVariantPicker(item, data);

      const preferredId =
        (item.dataset.productId && this.#variantChoiceByProductId.get(item.dataset.productId)) ||
        item.dataset.variantId;
      const variant = this.#findVariantById(data, preferredId) ?? this.#firstAvailableVariant(data);

      if (variant) this.#applyVariantToItem(item, variant, data, { skipPickerSync: false });
    }
  }

  /**
   * @param {HTMLElement} item
   * @returns {VapVariantData | null}
   */
  #parseVariantData(item) {
    const pid = item.dataset.productId;
    if (pid && this.#variantDataByProductId.has(pid)) {
      return this.#variantDataByProductId.get(pid) ?? null;
    }

    // Fallback: legacy script in gekloonde item (oude theme-versie)
    const script = item.querySelector('script[data-vap-variants]');
    if (!script?.textContent) {
      return null;
    }
    try {
      const parsed = JSON.parse(script.textContent);
      if (!parsed || !Array.isArray(parsed.variants)) return null;
      return /** @type {VapVariantData} */ (parsed);
    } catch {
      return null;
    }
  }

  /**
   * @param {VapVariantData} data
   * @returns {number}
   */
  #effectiveOptionCount(data) {
    if (data.options?.length) return data.options.length;
    return Math.max(0, ...data.variants.map((v) => v.options?.length ?? 0));
  }

  /**
   * @param {VapVariantData} data
   * @param {string | undefined} id
   * @returns {VapVariant | undefined}
   */
  #findVariantById(data, id) {
    if (!id) return undefined;
    return data.variants.find((v) => String(v.id) === String(id));
  }

  /**
   * @param {VapVariantData} data
   * @returns {VapVariant | undefined}
   */
  #firstAvailableVariant(data) {
    return data.variants.find((v) => v.available) ?? data.variants[0];
  }

  /**
   * Langste gemeenschappelijke suffix over alle optiewaarden (case-insensitive).
   * @param {string[]} values
   * @returns {{ display: string, raw: string }}
   */
  #detectOptionSuffix(values) {
    if (!values || values.length < 2) return { display: '', raw: '' };

    const trimmed = values.map((v) => String(v).trim());
    const minLen = Math.min(...trimmed.map((s) => s.length));

    for (let len = minLen; len > 0; len--) {
      const rawSuffix = trimmed[0].slice(-len);
      const lower = rawSuffix.toLowerCase();
      if (!trimmed.every((s) => s.toLowerCase().endsWith(lower))) continue;

      const display = rawSuffix.trim();
      if (!display) continue;
      if (!trimmed.every((s) => this.#stripOptionSuffix(s, rawSuffix).length > 0)) continue;

      return { display, raw: rawSuffix };
    }

    return { display: '', raw: '' };
  }

  /**
   * @param {string} value
   * @param {string} rawSuffix
   * @returns {string}
   */
  #stripOptionSuffix(value, rawSuffix) {
    const v = String(value).trim();
    const s = String(rawSuffix);
    if (!s) return v;
    if (v.toLowerCase().endsWith(s.toLowerCase())) {
      return v.slice(0, -s.length).trimEnd();
    }
    return v;
  }

  /**
   * @param {string} value
   * @param {string} rawSuffix
   * @returns {string}
   */
  #formatOptionDisplay(value, rawSuffix) {
    if (!rawSuffix) return value;
    const stripped = this.#stripOptionSuffix(value, rawSuffix);
    return stripped || value;
  }

  /**
   * @param {HTMLElement} item
   * @param {VapVariantData} data
   * @returns {'pills' | 'selects' | 'none'}
   */
  #buildVariantPicker(item, data) {
    const wrap = item.querySelector('[data-vap-variants]');
    if (!(wrap instanceof HTMLElement)) {
      return 'none';
    }

    wrap.replaceChildren();
    wrap.hidden = false;

    const optionCount = this.#effectiveOptionCount(data);
    if (optionCount <= 1) {
      wrap.classList.remove('vap-item__variants--selects');
      const values = this.#uniqueOptionValues(data, 0);
      if (!values.length) {
        wrap.hidden = true;
        return 'none';
      }

      const label = data.options?.[0] ?? 'Variant';
      const suffixInfo = this.#detectOptionSuffix(values);
      wrap.dataset.vapOptionSuffixRaw = suffixInfo.raw;

      const labelEl = document.createElement('span');
      labelEl.className = 'vap-item__variant-label';
      labelEl.textContent = label;

      const pillsWrap = document.createElement('div');
      pillsWrap.className = 'vap-item__variant-pills';
      pillsWrap.dataset.vapVariantPills = '';

      for (const value of values) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'vap-item__variant-pill';
        btn.dataset.vapVariantPill = '';
        btn.dataset.optionIndex = '0';
        btn.dataset.value = value;
        btn.textContent = this.#formatOptionDisplay(value, suffixInfo.raw);
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', `${label}: ${value}`);
        const hasAvailable = data.variants.some((v) => v.options[0] === value && v.available);
        if (!hasAvailable) btn.disabled = true;
        pillsWrap.appendChild(btn);
      }

      const suffixEl = document.createElement('span');
      suffixEl.className = 'vap-item__variant-suffix';
      suffixEl.dataset.vapVariantSuffix = '';
      if (suffixInfo.display) {
        suffixEl.textContent = suffixInfo.display;
      } else {
        suffixEl.hidden = true;
      }

      const valuesWrap = document.createElement('div');
      valuesWrap.className = 'vap-item__variant-values';
      valuesWrap.append(pillsWrap, suffixEl);

      const row = document.createElement('div');
      row.className = 'vap-item__variant-row';
      row.append(labelEl, valuesWrap);

      wrap.append(row);

      return 'pills';
    }

    wrap.classList.add('vap-item__variants--selects');
    wrap.removeAttribute('data-vap-option-suffix-raw');
    for (let i = 0; i < optionCount; i++) {
      const optionLabel = data.options?.[i] ?? `Optie ${i + 1}`;
      const values = this.#uniqueOptionValues(data, i);
      const suffixInfo = this.#detectOptionSuffix(values);

      const row = document.createElement('div');
      row.className = 'vap-item__variant-select-row';

      const labelEl = document.createElement('span');
      labelEl.className = 'vap-item__variant-label';
      labelEl.textContent = optionLabel;

      const select = document.createElement('select');
      select.className = 'vap-item__variant-select';
      select.dataset.vapVariantSelect = '';
      select.dataset.optionIndex = String(i);
      select.setAttribute('aria-label', optionLabel);

      for (const value of values) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = this.#formatOptionDisplay(value, suffixInfo.raw);
        select.appendChild(option);
      }

      row.append(labelEl, select);
      if (suffixInfo.display) {
        const suffixEl = document.createElement('span');
        suffixEl.className = 'vap-item__variant-suffix';
        suffixEl.textContent = suffixInfo.display;
        row.appendChild(suffixEl);
      }

      wrap.appendChild(row);
    }
    return 'selects';
  }

  /**
   * @param {VapVariantData} data
   * @param {number} optionIndex
   * @returns {string[]}
   */
  #uniqueOptionValues(data, optionIndex) {
    const seen = new Set();
    /** @type {string[]} */
    const values = [];
    for (const variant of data.variants) {
      const value = variant.options[optionIndex];
      if (!value || seen.has(value)) continue;
      seen.add(value);
      values.push(value);
    }
    return values;
  }

  /**
   * @param {VapVariantData} data
   * @param {string[]} selectedOptions
   * @returns {VapVariant | undefined}
   */
  #resolveVariant(data, selectedOptions) {
    const candidates = data.variants.filter((variant) =>
      selectedOptions.every((value, index) => !value || variant.options[index] === value)
    );
    if (!candidates.length) return this.#firstAvailableVariant(data);
    return candidates.find((v) => v.available) ?? candidates[0];
  }

  /**
   * @param {HTMLElement} item
   * @returns {string[]}
   */
  #readSelectedOptions(item) {
    const data = this.#variantDataByItem.get(item);
    const optionCount = data?.options?.length ?? 0;
    /** @type {string[]} */
    const selected = new Array(optionCount).fill('');

    const pill = item.querySelector('[data-vap-variant-pill][aria-pressed="true"]');
    if (pill instanceof HTMLButtonElement) {
      const index = parseInt(pill.dataset.optionIndex ?? '0', 10);
      selected[index] = pill.dataset.value ?? '';
      return selected;
    }

    for (const select of item.querySelectorAll('[data-vap-variant-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const index = parseInt(select.dataset.optionIndex ?? '0', 10);
      selected[index] = select.value;
    }

    return selected;
  }

  /**
   * @param {HTMLElement} item
   * @param {VapVariant} variant
   * @param {VapVariantData} data
   * @param {{ skipPickerSync?: boolean }} [options]
   */
  #applyVariantToItem(item, variant, data, options = {}) {
    const available = Boolean(variant.available);
    item.dataset.variantId = String(variant.id);
    item.dataset.price = String(variant.price);
    item.dataset.available = available ? 'true' : 'false';
    if (variant.productUrl) {
      item.dataset.productUrl = variant.productUrl;
    }
    item.classList.toggle('vap-item--unavailable', !available);

    const unitLabel = (this.dataset.unitLabel || 'stuk').trim();
    const unitEl = item.querySelector('.vap-item__unit');
    if (unitEl instanceof HTMLElement) {
      unitEl.textContent = available
        ? `${this.#formatMoney(variant.price)} per ${unitLabel}`
        : 'Niet leverbaar';
    }

    const imageUrl = variant.image || data.fallbackImage || '';
    const thumb = item.querySelector('.vap-item__thumb');
    if (thumb instanceof HTMLElement) {
      let img = thumb.querySelector('img');
      if (imageUrl) {
        if (!(img instanceof HTMLImageElement)) {
          img = document.createElement('img');
          img.loading = 'lazy';
          img.alt = '';
          thumb.replaceChildren(img);
        }
        img.src = imageUrl;
        img.removeAttribute('srcset');
      } else if (img) {
        img.remove();
      }

      if (available) {
        thumb.setAttribute('role', 'button');
        thumb.setAttribute('tabindex', '0');
        thumb.removeAttribute('aria-hidden');
        const title = item.dataset.productTitle ?? '';
        thumb.setAttribute('aria-label', `Bekijk ${title}`);
      } else {
        thumb.removeAttribute('role');
        thumb.removeAttribute('tabindex');
        thumb.setAttribute('aria-hidden', 'true');
        thumb.removeAttribute('aria-label');
      }
    }

    const nameEl = item.querySelector('.vap-item__name');
    if (nameEl instanceof HTMLElement) {
      if (available) {
        nameEl.setAttribute('role', 'button');
        nameEl.setAttribute('tabindex', '0');
        const title = item.dataset.productTitle ?? '';
        nameEl.setAttribute('aria-label', `Bekijk ${title}`);
      } else {
        nameEl.removeAttribute('role');
        nameEl.removeAttribute('tabindex');
        nameEl.removeAttribute('aria-label');
      }
    }

    if (!available && this.#itemQty(item) > 0) {
      this.#setItemSelected(item, false);
    }

    if (!options.skipPickerSync) {
      this.#syncPickerUi(item, variant, data);
    }

    const pid = item.dataset.productId;
    if (pid) this.#variantChoiceByProductId.set(pid, String(variant.id));

    this.#recompute();
  }

  /**
   * @param {HTMLElement} item
   * @param {VapVariant} variant
   * @param {VapVariantData} data
   */
  #syncPickerUi(item, variant, data) {
    const wrap = item.querySelector('[data-vap-variants]');
    const rawSuffix = wrap instanceof HTMLElement ? wrap.dataset.vapOptionSuffixRaw ?? '' : '';
    const labelEl = item.querySelector('.vap-item__variant-label');
    const label = labelEl instanceof HTMLElement ? labelEl.textContent ?? 'Variant' : 'Variant';

    for (const pill of item.querySelectorAll('[data-vap-variant-pill]')) {
      if (!(pill instanceof HTMLButtonElement)) continue;
      const index = parseInt(pill.dataset.optionIndex ?? '0', 10);
      const fullValue = pill.dataset.value ?? '';
      const pressed = variant.options[index] === fullValue;
      pill.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      pill.textContent = this.#formatOptionDisplay(fullValue, rawSuffix);
      pill.setAttribute('aria-label', `${label}: ${fullValue}`);
    }

    for (const select of item.querySelectorAll('[data-vap-variant-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const index = parseInt(select.dataset.optionIndex ?? '0', 10);
      const value = variant.options[index];
      if (value && [...select.options].some((opt) => opt.value === value)) {
        select.value = value;
      }
    }

    if (this.#effectiveOptionCount(data) > 1) {
      this.#refreshSelectOptions(item, data);
    }
  }

  /**
   * Ververs beschikbaarheid van opties in multi-select pickers.
   * @param {HTMLElement} item
   * @param {VapVariantData} data
   */
  #refreshSelectOptions(item, data) {
    const selected = this.#readSelectedOptions(item);

    for (const select of item.querySelectorAll('[data-vap-variant-select]')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const index = parseInt(select.dataset.optionIndex ?? '0', 10);
      const current = select.value;

      for (const option of select.options) {
        const trial = [...selected];
        while (trial.length <= index) trial.push('');
        trial[index] = option.value;
        const match = this.#resolveVariant(data, trial);
        option.disabled = !match?.available;
      }

      if (current) select.value = current;
    }
  }

  /**
   * Vul de ingeklapte kop, zodat zichtbaar is wát er in het paneel zit:
   * titel + namen van de items + de eerste paar productthumbnails.
   */
  #updateSummaryPreview() {
    const live = this.querySelector('[data-vap-live]');
    const sub = this.querySelector('[data-vap-sub]');
    if (!(live instanceof HTMLElement) || !(sub instanceof HTMLElement)) return;

    const items = [...live.querySelectorAll('[data-vap-item]')];
    sub.textContent = items.length ? 'Kies toebehoren' : '';

    const names = this.querySelector('[data-vap-names]');
    if (names instanceof HTMLElement) {
      names.textContent = items
        .map((item) => shortenTitle(item.querySelector('.vap-item__name')?.textContent ?? ''))
        .filter(Boolean)
        .join(', ');
    }

    const thumbs = this.querySelector('[data-vap-thumbs]');
    if (thumbs instanceof HTMLElement) {
      thumbs.replaceChildren(
        ...items.slice(0, PREVIEW_THUMB_COUNT).flatMap((item) => {
          const src = item.querySelector('.vap-item__thumb img')?.getAttribute('src');
          if (!src) return [];
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.loading = 'lazy';
          return [img];
        })
      );
    }
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-vap-variants]')) {
      event.stopPropagation();

      const pill = target.closest('[data-vap-variant-pill]');
      if (pill instanceof HTMLButtonElement && !pill.disabled) {
        event.preventDefault();
        this.#onVariantPillClick(pill);
      }
      return;
    }

    if (target.closest('[data-vap-inc]')) {
      this.#nudgeQty(target, +1);
      return;
    }
    if (target.closest('[data-vap-dec]')) {
      this.#nudgeQty(target, -1);
      return;
    }

    if (target.closest('.vap-item__check') || target.closest('[data-vap-check]')) {
      event.stopPropagation();
      return;
    }

    if (target.closest('[data-vap-stepper]')) return;

    const thumbOrName = target.closest('.vap-item__thumb, .vap-item__name');
    if (thumbOrName) {
      const item = thumbOrName.closest('[data-vap-item]');
      if (item instanceof HTMLElement && item.dataset.available === 'true') {
        event.preventDefault();
        this.#selectItem(item);
        this.#openQuickAddForItem(item);
      }
      return;
    }

    const item = target.closest('[data-vap-item]');
    if (item instanceof HTMLElement && item.dataset.available === 'true') {
      this.#toggleItemSelection(item);
    }
  };

  /**
   * Zet item geselecteerd (vinkje aan, aantal minimaal 1) zonder te togglen.
   * @param {HTMLElement} item
   */
  #selectItem(item) {
    const checkbox = item.querySelector('[data-vap-check]');
    const qtyInput = item.querySelector('[data-vap-qty]');
    if (!(checkbox instanceof HTMLInputElement) || !(qtyInput instanceof HTMLInputElement)) return;

    const wasSelected = checkbox.checked && this.#rawQty(qtyInput) > 0;
    if (!checkbox.checked) {
      checkbox.checked = true;
      if (this.#rawQty(qtyInput) === 0) qtyInput.value = '1';
      this.#recompute();
    } else if (this.#rawQty(qtyInput) === 0) {
      qtyInput.value = '1';
      this.#recompute();
    }

    if (!wasSelected) checkbox.blur();
  }

  /**
   * @param {HTMLElement} item
   */
  async #openQuickAddForItem(item) {
    if (!this.#quickAdd) return;
    const url = item.dataset.productUrl;
    if (!url) return;

    await this.#quickAdd.openForProduct({
      url,
      variantId: item.dataset.variantId,
      title: item.dataset.productTitle,
      description: item.dataset.productDescription,
    });
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (!target.matches('.vap-item__thumb, .vap-item__name')) return;

    const item = target.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement) || item.dataset.available !== 'true') return;

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.#selectItem(item);
      this.#openQuickAddForItem(item);
    }
  };

  /**
   * Toggle selectie via vinkje + aantal (0/1).
   * @param {HTMLElement} item
   */
  #toggleItemSelection(item) {
    const checkbox = item.querySelector('[data-vap-check]');
    if (!(checkbox instanceof HTMLInputElement)) return;
    checkbox.checked = !checkbox.checked;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    checkbox.blur();
  }

  /**
   * @param {HTMLButtonElement} pill
   */
  #onVariantPillClick(pill) {
    const item = pill.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement)) return;

    const data = this.#variantDataByItem.get(item);
    if (!data) return;

    const index = parseInt(pill.dataset.optionIndex ?? '0', 10);
    const selected = this.#readSelectedOptions(item);
    while (selected.length < data.options.length) selected.push('');
    selected[index] = pill.dataset.value ?? '';

    const variant = this.#resolveVariant(data, selected);
    if (variant) this.#applyVariantToItem(item, variant, data);
  }

  /**
   * @param {HTMLSelectElement} select
   */
  #onVariantSelectChange(select) {
    const item = select.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement)) return;

    const data = this.#variantDataByItem.get(item);
    if (!data) return;

    const selected = this.#readSelectedOptions(item);
    const variant = this.#resolveVariant(data, selected);
    if (variant) this.#applyVariantToItem(item, variant, data);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;

    if (target instanceof HTMLSelectElement && target.matches('[data-vap-variant-select]')) {
      event.stopPropagation();
      this.#onVariantSelectChange(target);
      return;
    }

    if (!(target instanceof HTMLInputElement) || !target.matches('[data-vap-check]')) return;

    const item = target.closest('[data-vap-item]');
    if (!(item instanceof HTMLElement)) return;

    const qtyInput = item.querySelector('[data-vap-qty]');
    if (!(qtyInput instanceof HTMLInputElement)) return;

    if (target.checked) {
      if (this.#rawQty(qtyInput) === 0) qtyInput.value = '1';
    } else {
      qtyInput.value = '0';
    }
    this.#recompute();
  };

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || !target.matches('[data-vap-qty]')) return;

    target.value = target.value.replace(/[^\d]/g, '');
    const item = target.closest('[data-vap-item]');
    if (item instanceof HTMLElement) this.#syncCheckboxFromQty(item);
    this.#recompute();
  };

  /**
   * @param {HTMLElement} item
   * @param {boolean} selected
   */
  #setItemSelected(item, selected) {
    const qtyInput = item.querySelector('[data-vap-qty]');
    const checkbox = item.querySelector('[data-vap-check]');
    if (qtyInput instanceof HTMLInputElement) qtyInput.value = selected ? '1' : '0';
    if (checkbox instanceof HTMLInputElement) checkbox.checked = selected;
  }

  /**
   * @param {HTMLElement} item
   */
  #syncCheckboxFromQty(item) {
    const qtyInput = item.querySelector('[data-vap-qty]');
    const checkbox = item.querySelector('[data-vap-check]');
    if (!(qtyInput instanceof HTMLInputElement) || !(checkbox instanceof HTMLInputElement)) return;
    checkbox.checked = this.#rawQty(qtyInput) > 0;
  }

  /**
   * @param {Element} fromEl
   * @param {number} delta
   */
  #nudgeQty(fromEl, delta) {
    const stepper = fromEl.closest('[data-vap-stepper]');
    const input = stepper?.querySelector('[data-vap-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(Math.max(0, this.#rawQty(input) + delta));
    const item = input.closest('[data-vap-item]');
    if (item instanceof HTMLElement) this.#syncCheckboxFromQty(item);
    this.#recompute();
  }

  /** @param {HTMLInputElement} input */
  #rawQty(input) {
    return Math.max(0, parseInt(input.value, 10) || 0);
  }

  /**
   * @param {HTMLElement} item
   * @returns {number}
   */
  #itemQty(item) {
    if (item.dataset.available !== 'true') return 0;
    const input = item.querySelector('[data-vap-qty]');
    return input instanceof HTMLInputElement ? this.#rawQty(input) : 0;
  }

  /**
   * Geselecteerde (aantal ≥ 1), leverbare items uit de live-container.
   * @param {ParentNode} scope
   * @returns {HTMLElement[]}
   */
  #selectedItems(scope) {
    return /** @type {HTMLElement[]} */ (
      [...scope.querySelectorAll('[data-vap-item]')].filter(
        (el) => el instanceof HTMLElement && this.#itemQty(el) > 0
      )
    );
  }

  /** Werk de geselecteerde-staat (class), de kop-telling en de footer-hint bij. */
  #recompute() {
    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    let count = 0;
    let totalCents = 0;
    for (const item of live.querySelectorAll('[data-vap-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const qty = this.#itemQty(item);
      const on = qty > 0;
      item.classList.toggle('vap-item--selected', on);
      if (on) {
        count += 1;
        totalCents += (parseInt(item.dataset.price ?? '0', 10) || 0) * qty;
      }
    }

    const selectedEl = this.querySelector('[data-vap-selected]');
    if (selectedEl instanceof HTMLElement) {
      if (count) {
        const label = count === 1 ? '1 gekozen' : `${count} gekozen`;
        selectedEl.textContent = `${label} · ${this.#formatMoney(totalCents)}`;
        selectedEl.hidden = false;
      } else {
        selectedEl.textContent = '';
        selectedEl.hidden = true;
      }
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return formatMoneyFromCents(cents, this.dataset.moneyFormat || '{{amount}}').replace(',00', ',-');
  }

  /**
   * Voeg geselecteerde toebehoren toe na succesvolle hoofd-add.
   * @returns {Promise<boolean>}
   */
  async #batchSelectedAccessoriesToCart() {
    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return false;

    const selected = this.#selectedItems(live);
    if (!selected.length) return false;

    const items = selected
      .map((item) => ({ id: item.dataset.variantId ?? '', quantity: this.#itemQty(item) }))
      .filter((line) => line.id && line.quantity > 0);
    if (!items.length) return false;

    try {
      const result = await this.#postCartAdd(items);
      document.dispatchEvent(
        new CartAddEvent(result, this.id || 'variant-additional-products', {
          source: 'variant-additional-products',
          itemCount: result.item_count,
          sections: result.sections,
        })
      );
      for (const item of selected) this.#setItemSelected(item, false);
      this.#recompute();
      return true;
    } catch (error) {
      console.error('Aanvullende artikelen — meenemen bij In winkelwagen mislukt:', error);
      return false;
    }
  }

  /**
   * CartAddEvent: batch bij hoofd-PDP add, uitvinken bij quick-add add voor VAP-item.
   * @param {Event} event
   */
  #onCartAdd = async (event) => {
    if (this.hidden) return;
    if (!(event instanceof CartAddEvent)) return;

    const data = event.detail?.data ?? {};
    if (data.didError) return;
    if (data.source === 'variant-additional-products') return;

    if (data.source !== 'product-form-component') return;

    const addedVariantId = String(event.detail.sourceId ?? data.variantId ?? '');
    if (!addedVariantId) return;

    const live = this.querySelector('[data-vap-live]');
    if (!(live instanceof HTMLElement)) return;

    const isQuickAddModal =
      event.target instanceof Element &&
      Boolean(event.target.closest('.quick-add-modal, #quick-add-dialog'));

    if (isQuickAddModal) {
      const vapItem = live.querySelector(`[data-vap-item][data-variant-id="${CSS.escape(addedVariantId)}"]`);
      if (vapItem instanceof HTMLElement) {
        this.#setItemSelected(vapItem, false);
        this.#recompute();
      }
      return;
    }

    const mainVariantId = this.#resolveMainVariantId();
    if (!mainVariantId || addedVariantId !== String(mainVariantId)) return;

    await this.#batchSelectedAccessoriesToCart();
  };

  /**
   * Configurator-PDP: batch na addToCart:success (ConfiguratorNestedCart omzeilt product-form CartAddEvent).
   * @param {Event} event
   */
  #onConfiguratorCartSuccess = async (event) => {
    if (this.hidden) return;

    const form = event.detail?.form;
    if (!(form instanceof HTMLFormElement)) return;
    if (!this.#isConfiguratorForm(form)) return;
    if (!this.#isFormInProductScope(form)) return;
    if (form.closest('.quick-add-modal, #quick-add-dialog')) return;

    const addedVariantId = this.#resolveMainVariantIdFromForm(form);
    if (!addedVariantId) return;

    const mainVariantId = this.#resolveMainVariantId();
    if (!mainVariantId || addedVariantId !== String(mainVariantId)) return;

    await this.#batchSelectedAccessoriesToCart();
  };

  /**
   * @param {Array<{ id: string, quantity: number }>} items
   * @param {boolean} [retryStringIds]
   * @returns {Promise<any>}
   */
  async #postCartAdd(items, retryStringIds = false) {
    const sectionIds = getBundledCartSectionIds();
    /** @type {Record<string, unknown>} */
    const body = {
      items: items.map((line) => ({
        id: retryStringIds ? line.id : Number(line.id) || line.id,
        quantity: line.quantity,
      })),
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const text = await response.text();
    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error('Ongeldige reactie van winkelmand');
    }

    if (!response.ok || data.status) {
      if (response.status === 422 && !retryStringIds) return this.#postCartAdd(items, true);
      throw new Error(data.description || data.message || 'Toevoegen aan winkelmand mislukt');
    }

    return data;
  }
}

if (!customElements.get('variant-additional-products')) {
  customElements.define('variant-additional-products', VariantAdditionalProducts);
}
